import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {AnswerOption} from '../../../data/models/test/answer';
import {FormBuilder, FormGroup} from '@angular/forms';
import {ANSWER_OPTION_VALIDATORS} from '../../../../constants/validators';
import {AnswerService} from '../../../services/api/answer.service';
import {MatDialog} from '@angular/material/dialog';
import {AnswerOptionDeleteDialogComponent} from '../../dialogs/answer-option-delete-dialog/answer-option-delete-dialog.component';

@Component({
  selector: 'app-answer-option-edit-form',
  templateUrl: './answer-option-edit-form.component.html',
  styleUrls: ['./answer-option-edit-form.component.css']
})
export class AnswerOptionEditFormComponent implements OnInit {

  @Input() readonly = false;
  @Input() testId!: number;
  @Input() questionId!: number;
  @Input() option!: AnswerOption;
  @Input() allOptions: AnswerOption[] = [];

  @ViewChild('input') inputElement!: ElementRef;

  @Output() delete = new EventEmitter<void>();
  @Output() update = new EventEmitter<AnswerOption>();

  public form!: FormGroup;
  public editMode = false;

  constructor(
    public dialog: MatDialog,
    private fb: FormBuilder,
    private answerService: AnswerService,
  ) {
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      text: [this.option.text, ANSWER_OPTION_VALIDATORS.TEXT],
      blockedOptionIds: [this.getBlockedOptionIds()],
    });
  }

  setEditMode(value: boolean): void {
    this.editMode = value;
    if (value) {
      this.form.patchValue({
        blockedOptionIds: this.getBlockedOptionIds(),
      }, {emitEvent: false});
      setTimeout(() => {
        this.inputElement.nativeElement.focus();
      }, 0);
    }
  }

  onEdit(event: MouseEvent): void {
    event.preventDefault();
    if (!this.readonly) {
      this.setEditMode(true);
    }
  }

  onDelete(event: MouseEvent): void {
    event.preventDefault();
    if (!this.readonly) {
      this.openOptionDeleteDialog();
    }
  }

  onSubmit(event: MouseEvent): void {
    event.preventDefault();
    if (this.form.valid) {
      const text = this.form.controls.text.value;
      const blockedOptionIds = this.normalizeBlockedIds(this.form.controls.blockedOptionIds.value);
      const textChanged = this.option.text !== text;
      const blockedChanged = !this.areBlockedOptionIdsEqual(blockedOptionIds);

      if (!textChanged && !blockedChanged) {
        this.setEditMode(false);
        return;
      }

      const payload: {
        text?: string;
        blockedOptionIds?: number[];
      } = {};

      if (textChanged) {
        payload.text = text;
      }

      if (blockedChanged) {
        payload.blockedOptionIds = blockedOptionIds;
      }

      this.answerService.updateAnswerOption(this.testId, this.questionId, this.option.id, payload)
        .then(option => {
          this.option = option;
          this.form.patchValue({
            text: option.text,
            blockedOptionIds: this.getBlockedOptionIds(),
          }, {emitEvent: false});
          this.update.emit(option);
          this.setEditMode(false);
        });
    }
  }

  onCancel(event: MouseEvent): void {
    event.preventDefault();
    this.form.controls.text.setValue(this.option.text);
    this.form.controls.blockedOptionIds.setValue(this.getBlockedOptionIds());
    this.setEditMode(false);
  }

  openOptionDeleteDialog(): void {
    const dialogRef = this.dialog.open(AnswerOptionDeleteDialogComponent, {
      data: {
        testId: this.testId,
        questionId: this.questionId,
        option: this.option,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.delete.emit();
      }
    });
  }

  get availableOptions(): AnswerOption[] {
    if (!this.allOptions) {
      return [];
    }
    return this.allOptions.filter(option => option.id !== this.option.id);
  }

  get blockedOptionNames(): string[] {
    const ids = this.getBlockedOptionIds();
    return ids
      .map(id => this.allOptions?.find(option => option.id === id)?.text)
      .filter((text): text is string => !!text);
  }

  private areBlockedOptionIdsEqual(ids: number[]): boolean {
    const current = this.getBlockedOptionIds();
    if (ids.length !== current.length) {
      return false;
    }
    const sortedIds = [...ids].sort();
    const sortedCurrent = [...current].sort();
    return sortedIds.every((value, index) => value === sortedCurrent[index]);
  }

  private getBlockedOptionIds(): number[] {
    return this.option.blockedOptionIds ? [...this.option.blockedOptionIds] : [];
  }

  private normalizeBlockedIds(value: any): number[] {
    if (!Array.isArray(value)) {
      return [];
    }
    const normalized = value
      .map(id => typeof id === 'number' ? id : Number(id))
      .filter(id => typeof id === 'number' && !Number.isNaN(id));
    return Array.from(new Set(normalized));
  }

}
